import pickUrl from './mine-pick.mp3';

/**
 * The sound of the mine (§18).
 *
 * The strikes are Igu's own recording now (mine-pick.mp3: a pick on rock, cut
 * from pico_rocas to its 2.2 seconds of hits so it loops as one steady working
 * rhythm), played on a loop while any miner is on shift. The synthesised
 * strikes described below stay as the fallback for a device that cannot decode
 * the file, and they fill the moment before the file has loaded.
 *
 * The rest of this note is about those synthesised strikes. Synthesised rather than sampled. The app already fetches sixteen megabytes
 * of artwork before it opens; a loop of pick strikes would be another few
 * hundred kilobytes for something the Web Audio API can build from noise and
 * a filter, and a synthesised strike can be varied on every swing so it never
 * reads as the same file playing again.
 *
 * What a pick hitting rock actually is, and how each part is made here:
 *
 *   - **The crack.** The tip arriving. A very short burst of noise through a
 *     high bandpass, decaying in about thirty milliseconds. This is the part
 *     the ear uses to place the hit.
 *   - **The body.** The mass of stone answering. Low-passed noise around
 *     200–500 Hz with a longer tail, which is what makes it rock rather than
 *     a click.
 *   - **The ring.** Steel. Two inharmonic partials up around 2–3 kHz, quiet,
 *     decaying fast. Inharmonic on purpose: a harmonic pair sounds like a
 *     bell or a note, and a pick is neither.
 *   - **The debris.** A handful of tiny grains scattered over the next
 *     quarter second — the chips coming loose. Without them a strike sounds
 *     like it happened in a padded room.
 *
 * Under all of it sits an air drone: brown-ish noise through a low-pass, very
 * quiet, which is what a deep space sounds like when nothing is happening.
 *
 * Everything below obeys four rules that come from the platform rather than
 * from taste:
 *
 *   1. **Nothing starts without a gesture.** Browsers refuse to start audio
 *      otherwise, and a page that begins making noise on load is a page people
 *      close. The context is created on the first interaction and nowhere else.
 *   2. **One engine.** A second one would layer a second mine over the first.
 *   3. **It stops when the work stops** — and when the tab is hidden, because
 *      a mine running in a pocket is a battery complaint.
 *   4. **It is off unless the user turned it on.** Sound is opt-in.
 */

export interface MineAudioOptions {
  /** 0 to 1. The user's setting, not a mixing decision. */
  volume: number;
}

/** Master ceiling. The drone and the strikes are balanced under this. */
const MASTER_GAIN = 0.5;

/** The recorded strikes under the master: loud and present, the drone stays under them. */
const PICK_GAIN = 0.9;

/** Seconds between swings, before humanising. A working rhythm, not a drum. */
const SWING_INTERVAL = 1.15;

/**
 * How much each swing is nudged, as a fraction of the interval.
 *
 * Without this the strikes land on a grid and stop sounding like a person.
 * With too much they stop sounding like work.
 */
const SWING_JITTER = 0.22;

function randomBetween(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

/**
 * A short buffer of white noise, made once and reused.
 *
 * Every strike is a slice of this through a different filter and envelope, so
 * one second of samples is enough for a session's worth of distinct hits.
 */
function createNoiseBuffer(context: AudioContext, seconds = 1): AudioBuffer {
  const frames = Math.floor(context.sampleRate * seconds);
  const buffer = context.createBuffer(1, frames, context.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < frames; i += 1) data[i] = Math.random() * 2 - 1;
  return buffer;
}

/**
 * Brown noise: white noise integrated, which tilts the spectrum down about
 * 6 dB per octave. That downward tilt is what makes it read as air and
 * distance rather than as hiss.
 */
function createBrownNoiseBuffer(context: AudioContext, seconds = 4): AudioBuffer {
  const frames = Math.floor(context.sampleRate * seconds);
  const buffer = context.createBuffer(1, frames, context.sampleRate);
  const data = buffer.getChannelData(0);
  let last = 0;
  for (let i = 0; i < frames; i += 1) {
    const white = Math.random() * 2 - 1;
    last = (last + 0.02 * white) / 1.02;
    data[i] = last * 3.5;
  }
  return buffer;
}

export class MineAudio {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private brown: AudioBuffer | null = null;

  private drone: { source: AudioBufferSourceNode; gain: GainNode } | null = null;
  private swingTimer: number | null = null;
  /** The recorded strikes, decoded once; null until loaded, or if this device cannot decode them. */
  private pick: AudioBuffer | null = null;
  private pickLoop: { source: AudioBufferSourceNode; gain: GainNode } | null = null;

  private enabled = false;
  private working = false;
  private volume = 0.7;
  /** True once a real user gesture has let us create the context. */
  private unlocked = false;

  //---------------------------------------------------------------------------
  // Lifecycle
  //---------------------------------------------------------------------------

  /**
   * Called from a real user gesture — a tap, a click, a key.
   *
   * Creating an AudioContext outside one leaves it suspended on every mobile
   * browser, and a suspended context that nobody resumes is silence with no
   * error to explain it.
   */
  unlock(): void {
    if (this.unlocked) {
      // A context can be suspended again by the system; a later gesture is
      // the only thing allowed to bring it back.
      void this.context?.resume().catch(() => {});
      return;
    }

    const Ctor: typeof AudioContext | undefined =
      window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;

    try {
      const context = new Ctor();
      const master = context.createGain();
      master.gain.value = 0;
      master.connect(context.destination);

      this.context = context;
      this.master = master;
      this.noise = createNoiseBuffer(context);
      this.brown = createBrownNoiseBuffer(context);
      this.unlocked = true;

      this.applyVolume();
      void this.loadPick(context);
      if (this.enabled && this.working) this.start();
    } catch {
      // No audio on this device. The app is not worse for it.
    }
  }

  /**
   * The recorded strikes, fetched and decoded once. If the mine is already
   * running on the synthesised strikes when they arrive, it switches to them.
   */
  private async loadPick(context: AudioContext): Promise<void> {
    try {
      const response = await fetch(pickUrl);
      const data = await response.arrayBuffer();
      const buffer = await context.decodeAudioData(data);
      if (this.context !== context) return;
      this.pick = buffer;
      if (this.enabled && this.working && this.drone && !this.pickLoop) {
        if (this.swingTimer !== null) {
          window.clearTimeout(this.swingTimer);
          this.swingTimer = null;
        }
        this.startPickLoop();
      }
    } catch {
      // Cannot decode it here: the synthesised strikes carry on.
    }
  }

  /** The user's switch. Off stops everything at once. */
  setEnabled(enabled: boolean): void {
    if (this.enabled === enabled) return;
    this.enabled = enabled;
    if (!enabled) {
      this.stop();
      return;
    }
    if (this.working) this.start();
  }

  setVolume(volume: number): void {
    this.volume = Math.min(1, Math.max(0, volume));
    this.applyVolume();
  }

  /**
   * Whether any miner is swinging.
   *
   * Idempotent on purpose: the mine screen re-renders on every countdown tick,
   * and calling this with the same value must not restart anything.
   */
  setWorking(working: boolean): void {
    if (this.working === working) return;
    this.working = working;
    if (working && this.enabled) this.start();
    else this.stop();
  }

  /** Releases the device's audio hardware. */
  async dispose(): Promise<void> {
    this.stop();
    const context = this.context;
    this.context = null;
    this.master = null;
    this.unlocked = false;
    try {
      await context?.close();
    } catch {
      // Already closed.
    }
  }

  //---------------------------------------------------------------------------
  // The mine running
  //---------------------------------------------------------------------------

  private applyVolume(): void {
    const { context, master } = this;
    if (!context || !master) return;
    const target = this.enabled && this.working ? this.volume * MASTER_GAIN : 0;
    // Ramped, never set: a gain that jumps produces a click, which on a quiet
    // ambient bed is more noticeable than the bed itself.
    master.gain.cancelScheduledValues(context.currentTime);
    master.gain.setTargetAtTime(target, context.currentTime, 0.08);
  }

  private start(): void {
    if (!this.context || !this.master || this.drone) return;
    this.startDrone();
    // The recording when it is here; the synthesised swings until it is.
    if (this.pick) this.startPickLoop();
    else this.scheduleSwing();
    this.applyVolume();
  }

  /** The recorded strikes, on a loop, faded in so the first one does not jump out. */
  private startPickLoop(): void {
    const { context, master, pick } = this;
    if (!context || !master || !pick || this.pickLoop) return;
    const source = context.createBufferSource();
    source.buffer = pick;
    source.loop = true;
    const gain = context.createGain();
    gain.gain.value = 0;
    gain.gain.setTargetAtTime(PICK_GAIN, context.currentTime, 0.05);
    source.connect(gain).connect(master);
    source.start();
    this.pickLoop = { source, gain };
  }

  private stop(): void {
    if (this.swingTimer !== null) {
      window.clearTimeout(this.swingTimer);
      this.swingTimer = null;
    }
    this.applyVolume();

    const pickLoop = this.pickLoop;
    this.pickLoop = null;
    if (pickLoop && this.context) {
      pickLoop.gain.gain.setTargetAtTime(0, this.context.currentTime, 0.08);
      try {
        pickLoop.source.stop(this.context.currentTime + 0.3);
      } catch {
        // Already stopped.
      }
    }

    const drone = this.drone;
    this.drone = null;
    if (!drone || !this.context) return;

    // Faded, then stopped: cutting a noise source dead is an audible thump.
    const end = this.context.currentTime + 0.4;
    drone.gain.gain.setTargetAtTime(0, this.context.currentTime, 0.12);
    try {
      drone.source.stop(end);
    } catch {
      // Already stopped.
    }
  }

  private startDrone(): void {
    const { context, master, brown } = this;
    if (!context || !master || !brown) return;

    const source = context.createBufferSource();
    source.buffer = brown;
    source.loop = true;

    // Distance and rock: everything above a few hundred hertz has been
    // absorbed by the time it reaches you.
    const lowpass = context.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.value = 340;
    lowpass.Q.value = 0.7;

    const gain = context.createGain();
    gain.gain.value = 0.16;

    source.connect(lowpass).connect(gain).connect(master);
    source.start();

    this.drone = { source, gain };
  }

  /** Queues the next swing at a humanised interval. */
  private scheduleSwing(): void {
    if (!this.enabled || !this.working) return;
    const jitter = SWING_INTERVAL * SWING_JITTER;
    const delay = randomBetween(SWING_INTERVAL - jitter, SWING_INTERVAL + jitter);
    this.swingTimer = window.setTimeout(() => {
      this.strike();
      this.scheduleSwing();
    }, delay * 1000);
  }

  //---------------------------------------------------------------------------
  // One swing
  //---------------------------------------------------------------------------

  private strike(): void {
    const { context, master, noise } = this;
    if (!context || !master || !noise) return;
    // A hidden tab keeps its timers but should make no sound.
    if (document.hidden) return;

    const at = context.currentTime;
    // Every strike is a slightly different hit: the pick does not land the
    // same way twice, and identical strikes are what make a loop sound looped.
    const force = randomBetween(0.75, 1);

    this.crack(at, force);
    this.body(at, force);
    if (Math.random() < 0.55) this.ring(at, force);
    this.debris(at, force);
  }

  /** The tip arriving: the part that places the hit. */
  private crack(at: number, force: number): void {
    const { context, master, noise } = this;
    if (!context || !master || !noise) return;

    const source = context.createBufferSource();
    source.buffer = noise;
    // A different slice each time, so no two cracks are the same samples.
    const offset = Math.random() * (noise.duration - 0.06);

    const band = context.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = randomBetween(2300, 3600);
    band.Q.value = 1.4;

    const gain = context.createGain();
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(0.5 * force, at + 0.004);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.035);

    source.connect(band).connect(gain).connect(master);
    source.start(at, offset, 0.06);
    source.stop(at + 0.07);
  }

  /** The mass of stone answering — what makes it rock and not a click. */
  private body(at: number, force: number): void {
    const { context, master, noise } = this;
    if (!context || !master || !noise) return;

    const source = context.createBufferSource();
    source.buffer = noise;
    const offset = Math.random() * (noise.duration - 0.3);

    const lowpass = context.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.setValueAtTime(randomBetween(420, 620), at);
    // The stone dulls as the energy leaves it.
    lowpass.frequency.exponentialRampToValueAtTime(180, at + 0.22);
    lowpass.Q.value = 1.1;

    const gain = context.createGain();
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(0.42 * force, at + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.26);

    source.connect(lowpass).connect(gain).connect(master);
    source.start(at, offset, 0.3);
    source.stop(at + 0.32);
  }

  /**
   * Steel.
   *
   * Two partials at a deliberately non-integer ratio: a harmonic pair reads
   * as a bell or a note, and a pick is neither. 1 : 2.76 is around what a
   * struck bar gives.
   */
  private ring(at: number, force: number): void {
    const { context, master } = this;
    if (!context || !master) return;

    const root = randomBetween(1900, 2600);
    for (const [ratio, level] of [
      [1, 0.09],
      [2.76, 0.05],
    ] as const) {
      const osc = context.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = root * ratio;

      const gain = context.createGain();
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(level * force, at + 0.006);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + randomBetween(0.16, 0.3));

      osc.connect(gain).connect(master);
      osc.start(at);
      osc.stop(at + 0.36);
    }
  }

  /** Chips coming loose — without these the strike has no room around it. */
  private debris(at: number, force: number): void {
    const { context, master, noise } = this;
    if (!context || !master || !noise) return;

    const grains = 2 + Math.floor(Math.random() * 4);
    for (let i = 0; i < grains; i += 1) {
      const when = at + randomBetween(0.05, 0.3);

      const source = context.createBufferSource();
      source.buffer = noise;
      const offset = Math.random() * (noise.duration - 0.03);

      const band = context.createBiquadFilter();
      band.type = 'bandpass';
      band.frequency.value = randomBetween(1200, 4200);
      band.Q.value = 2.2;

      const gain = context.createGain();
      gain.gain.setValueAtTime(0.0001, when);
      gain.gain.exponentialRampToValueAtTime(randomBetween(0.03, 0.08) * force, when + 0.003);
      gain.gain.exponentialRampToValueAtTime(0.0001, when + 0.05);

      source.connect(band).connect(gain).connect(master);
      source.start(when, offset, 0.03);
      source.stop(when + 0.06);
    }
  }
}

/**
 * The one engine.
 *
 * A module-level instance rather than a React value, because two of these
 * would be two mines playing at once and because it has to outlive any
 * component that happens to be on screen.
 */
export const mineAudio = new MineAudio();
